import { tryParseUrl } from './url';

/**
 * Lightweight, client-side "is this a financial site?" classifier for the
 * password checkup, which floats financial logins to the top (one breach of a
 * bank/brokerage/payment account is the costliest).
 *
 * We have no site-type data in the signed site-rules bundle, so this is a
 * **hand-curated** list plus the regulated `.bank` gTLD (which only verified
 * banks can hold) — offline, private, deterministic, and precise by design: no
 * fuzzy keyword matching, so it never mis-classifies. A miss just leaves a login
 * in the normal order. Extend the list by hand as needed. (A richer,
 * server-authored `category` on the bundle can supersede this later.)
 */

/** Registrable (eTLD+1-ish) domains of well-known financial services. A host
 *  matches when it equals one of these or is a subdomain of it. Lower-case. */
export const FINANCIAL_DOMAINS: ReadonlySet<string> = new Set([
  // US banks
  'chase.com', 'bankofamerica.com', 'wellsfargo.com', 'usbank.com', 'citi.com',
  'citibank.com', 'capitalone.com', 'pnc.com', 'truist.com', 'tdbank.com', 'td.com',
  'ally.com', 'discover.com', 'americanexpress.com', 'usaa.com', 'navyfederal.org',
  'regions.com', 'fifththird.com', '53.com', 'keybank.com', 'citizensbank.com',
  'mtb.com', 'huntington.com', 'synchrony.com', 'synchronybank.com', 'marcus.com',
  'chime.com', 'sofi.com', 'schwabbank.com', 'firstrepublic.com',
  // US credit unions (largest; .bank gTLD covers verified banks, not CUs)
  'penfed.org', 'navyfederal.org', 'suncoastcreditunion.com', 'schoolsfirstfcu.org',
  'becu.org', 'alliantcreditunion.com', 'golden1.com',
  // International banks
  'hsbc.com', 'hsbc.co.uk', 'barclays.co.uk', 'barclays.com', 'santander.com',
  'santander.co.uk', 'lloydsbank.com', 'natwest.com', 'monzo.com', 'starlingbank.com',
  'rbc.com', 'td.ca', 'scotiabank.com', 'cibc.com', 'bmo.com', 'commbank.com.au',
  'nab.com.au', 'anz.com', 'westpac.com.au', 'ing.com', 'deutsche-bank.de',
  // Brokerages / investing
  'fidelity.com', 'schwab.com', 'vanguard.com', 'etrade.com', 'tdameritrade.com',
  'robinhood.com', 'merrilledge.com', 'interactivebrokers.com', 'webull.com',
  'wealthfront.com', 'betterment.com', 'm1.com', 'public.com',
  // Payments / transfer
  'paypal.com', 'venmo.com', 'cash.app', 'cashapp.com', 'zellepay.com', 'wise.com',
  'revolut.com', 'squareup.com', 'stripe.com', 'affirm.com', 'klarna.com',
  // Crypto exchanges / wallets
  'coinbase.com', 'binance.com', 'binance.us', 'kraken.com', 'gemini.com',
  'crypto.com', 'blockchain.com', 'ledger.com', 'blockfi.com',
  // Card networks / credit
  'visa.com', 'mastercard.com', 'creditkarma.com', 'experian.com', 'equifax.com',
  'transunion.com',
  // Personal finance / tax
  'intuit.com', 'turbotax.com', 'mint.com', 'quicken.com',
]);

/**
 * True when a host (e.g. `secure.chase.com`) belongs to a financial service:
 * the `.bank` gTLD (regulated — only verified banks hold it), or a curated
 * domain (or a subdomain of one). Hand-curated + precise; no fuzzy keywords.
 */
export function isFinancialHost(host: string): boolean {
  const h = host.trim().toLowerCase().replace(/^www\./, '');
  if (!h) return false;
  if (h.endsWith('.bank')) return true;
  for (const d of FINANCIAL_DOMAINS) {
    if (h === d || h.endsWith('.' + d)) return true;
  }
  return false;
}

/**
 * True when any of a login's web identifiers (URLs or bare domains) resolves to
 * a financial host. `tryParseUrl` tolerates schemeless values like `chase.com`.
 */
export function isFinancialItem(webIdentifiers: readonly string[]): boolean {
  return webIdentifiers.some((id) => {
    const host = tryParseUrl(id)?.hostname;
    return host ? isFinancialHost(host) : false;
  });
}
